// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {GlobalLimits} from '@app/features/app/utils/GlobalLimits';
import {EmojiListHeader, EmojiListItem} from '@app/features/emoji/components/emojis/EmojiListItem';
import {EmojiUploadModal} from '@app/features/emoji/components/modals/EmojiUploadModal';
import EmojiStickerLayout from '@app/features/emoji/state/EmojiStickerLayout';
import * as GuildEmojiCommands from '@app/features/expressions/commands/GuildEmojiCommands';
import {
	seedGuildEmojiCache,
	subscribeToGuildEmojiUpdates,
} from '@app/features/expressions/state/GuildExpressionTabCache';
import {
	getAcceptString,
	getAssetFormatErrorMessage,
	getSupportedFormatsLabel,
} from '@app/features/expressions/utils/AssetFormatCopy';
import * as ImageCropUtils from '@app/features/expressions/utils/ImageCropUtils';
import {CloneAllowedToggle} from '@app/features/guild/components/modals/guild_tabs/CloneAllowedToggle';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildEmojiTab.module.css';
import {UploadDropZone} from '@app/features/guild/components/UploadDropZone';
import {UploadSlotInfo} from '@app/features/guild/components/UploadSlotInfo';
import Guilds from '@app/features/guild/state/Guilds';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureValidationErrors} from '@app/features/platform/utils/ResponseInspection';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import Users from '@app/features/user/state/Users';
import {canCropFormat} from '@app/features/voice/utils/MediaCapabilities';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import type {GuildEmojiWithUser} from '@app/vendor/schema/domains/guild/GuildEmojiSchemas';
import {sortBySnowflakeDesc} from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const MAX_EMOJIS_PER_UPLOAD = 50;
const logger = new Logger('GuildEmojiTab');
const GuildEmojiTab: React.FC<{guildId: string}> = observer(function GuildEmojiTab({guildId}) {
	const {t, i18n} = useLingui();
	const [emojis, setEmojis] = useState<ReadonlyArray<GuildEmojiWithUser>>([]);
	const [fetchStatus, setFetchStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
	const [searchQuery, setSearchQuery] = useState('');
	const layoutState = EmojiStickerLayout;
	const layout = layoutState.getEmojiLayout();
	const guild = Guilds.getGuild(guildId);
	const canCreateExpressions = Permission.can(Permissions.CREATE_EXPRESSIONS, {guildId});
	const canManageExpressions = Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId});
	const currentUserId = Users.currentUserId;
	const setEmojisWithCache = useCallback(
		(updater: React.SetStateAction<ReadonlyArray<GuildEmojiWithUser>>) => {
			setEmojis((prev) => {
				const next =
					typeof updater === 'function'
						? (updater as (previous: ReadonlyArray<GuildEmojiWithUser>) => ReadonlyArray<GuildEmojiWithUser>)(prev)
						: updater;
				const frozen = Object.freeze(sortBySnowflakeDesc(next));
				seedGuildEmojiCache(guildId, frozen);
				return frozen;
			});
		},
		[guildId],
	);
	const fetchEmojis = useCallback(async () => {
		try {
			setFetchStatus('pending');
			const emojiList = await GuildEmojiCommands.list(guildId);
			setEmojisWithCache(emojiList);
			setFetchStatus('success');
		} catch (error) {
			logger.error('Failed to fetch emojis', error);
			setFetchStatus('error');
		}
	}, [guildId, setEmojisWithCache]);
	useEffect(() => {
		if (fetchStatus === 'idle') {
			void fetchEmojis();
		}
	}, [fetchStatus, fetchEmojis]);
	useEffect(() => {
		return subscribeToGuildEmojiUpdates(guildId, (updatedEmojis) => {
			setEmojisWithCache(updatedEmojis);
		});
	}, [guildId, setEmojisWithCache]);
	const filteredEmojis = useMemo(() => {
		if (!searchQuery) return emojis;
		return matchSorter(emojis, searchQuery, {keys: ['name']});
	}, [emojis, searchQuery]);
	const {staticEmojis, animatedEmojis} = useMemo(() => {
		const statics: Array<GuildEmojiWithUser> = [];
		const animateds: Array<GuildEmojiWithUser> = [];
		for (const emoji of filteredEmojis) {
			if (emoji.animated) {
				animateds.push(emoji);
			} else {
				statics.push(emoji);
			}
		}
		return {staticEmojis: statics, animatedEmojis: animateds};
	}, [filteredEmojis]);
	const maxStaticEmojis = useMemo(() => {
		if (!guild) return 50;
		if (guild.features.has('MORE_EMOJI')) return 250;
		return 50;
	}, [guild]);
	const maxAnimatedEmojis = useMemo(() => {
		if (!guild) return 50;
		if (guild.features.has('MORE_EMOJI')) return 250;
		return 50;
	}, [guild]);
	const canModifyEmoji = useCallback(
		(emoji: GuildEmojiWithUser): boolean => {
			if (canManageExpressions) return true;
			if (canCreateExpressions && emoji.user?.id === currentUserId) return true;
			return false;
		},
		[canManageExpressions, canCreateExpressions, currentUserId],
	);
	const handleEmojiDelete = useCallback((emojiId: string) => {
		setEmojis((prev) => prev.filter((e) => e.id !== emojiId));
	}, []);
	const handleEmojiRename = useCallback(
		async (emojiId: string, newName: string) => {
			try {
				await GuildEmojiCommands.update(guildId, emojiId, {name: newName});
				setEmojis((prev) => prev.map((e) => (e.id === emojiId ? {...e, name: newName} : e)));
			} catch (error) {
				logger.error('Failed to rename emoji', error);
			}
		},
		[guildId],
	);
	const handleFileSelect = useCallback(
		async (files: FileList | ReadonlyArray<File>) => {
			const maxStatic = maxStaticEmojis;
			const statics = filteredEmojis.filter((e) => !e.animated);
			const candidateFiles = Array.from(files);
			const decodableFiles: Array<File> = [];
			let hadUndecodable = false;
			for (const file of candidateFiles) {
				if (await canCropFormat(file.type)) {
					decodableFiles.push(file);
				} else {
					hadUndecodable = true;
				}
			}
			if (hadUndecodable) {
				ToastCommands.createToast({
					type: 'error',
					children: getAssetFormatErrorMessage(i18n, 'emoji', 'unsupported_mime'),
				});
			}
			const filesWithinSlots = decodableFiles.slice(0, maxStatic - statics.length);
			if (filesWithinSlots.length === 0) {
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={t`No emoji slots available`}
							description={
								<Trans>
									You've reached the maximum number of static emojis. Delete some existing emojis or upgrade your server
									to increase the limit.
								</Trans>
							}
							primaryText={t`Understood`}
							onPrimary={() => {}}
						/>
					)),
				);
				return;
			}
			ModalCommands.push(modal(() => <EmojiUploadModal count={filesWithinSlots.length} />));
			const preparedEmojis: Array<{name: string; image: string; file: File}> = [];
			const preparationFailures: Array<{name: string; error: string}> = [];
			for (const file of filesWithinSlots) {
				try {
					const maxEmojiSize = GlobalLimits.getEmojiMaxSize();
					const base64Image = await ImageCropUtils.optimizeEmojiImage(file, maxEmojiSize, 128);
					const name = GuildEmojiCommands.sanitizeEmojiName(file.name);
					preparedEmojis.push({name, image: base64Image, file});
				} catch (error) {
					logger.error(`Failed to prepare emoji ${file.name}`, error);
					preparationFailures.push({
						name: file.name,
						error: error instanceof Error ? error.message : String(error),
					});
				}
			}
			if (preparedEmojis.length === 0) {
				ModalCommands.pop();
				ModalCommands.push(
					modal(() => (
						<ConfirmModal
							title={t`Failed to Prepare Emojis`}
							description={
								<div className={styles.modalErrorContainer}>
									<p className={styles.modalErrorIntro}>
										<Trans>Unable to prepare any emojis for upload. The following errors occurred:</Trans>
									</p>
									{preparationFailures.map((failed, index) => (
										<div key={index} className={styles.modalErrorItem}>
											<div className={styles.modalErrorDetails}>
												<div className={styles.modalErrorName}>{failed.name}</div>
												<div className={styles.modalErrorMessage}>{failed.error}</div>
											</div>
										</div>
									))}
								</div>
							}
							primaryText={t`OK`}
							primaryVariant="primary"
							onPrimary={() => {}}
						/>
					)),
				);
				return;
			}
			const chunkedPreparedEmojis: Array<{
				chunk: Array<{name: string; image: string; file: File}>;
				startIndex: number;
			}> = [];
			for (let chunkStart = 0; chunkStart < preparedEmojis.length; chunkStart += MAX_EMOJIS_PER_UPLOAD) {
				chunkedPreparedEmojis.push({
					chunk: preparedEmojis.slice(chunkStart, chunkStart + MAX_EMOJIS_PER_UPLOAD),
					startIndex: chunkStart,
				});
			}
			let hasUploadError = false;
			for (const {chunk, startIndex} of chunkedPreparedEmojis) {
				try {
					const emojisToUpload = chunk.map(({name, image}) => ({name, image}));
					await GuildEmojiCommands.bulkUpload(guildId, emojisToUpload);
				} catch (error: unknown) {
					hasUploadError = true;
					logger.error(`Failed to upload emojis starting at index ${startIndex}`, error);
					const validationErrors = failureValidationErrors(error);
					const errorMsg =
						validationErrors?.map((e) => e.message).join(', ') ??
						(error instanceof Error ? error.message : null) ??
						t`Failed to upload emojis. Please try again.`;
					ToastCommands.createToast({
						type: 'error',
						children: (
							<Trans>
								Failed to upload emojis starting at #{startIndex + 1}: {errorMsg}
							</Trans>
						),
					});
				}
			}
			if (!hasUploadError) {
				await fetchEmojis();
			}
			ModalCommands.pop();
		},
		[guildId, filteredEmojis, fetchEmojis, maxStaticEmojis, i18n],
	);
	const handleDrop = useCallback(
		async (files: Array<File>) => {
			if (files.length > 0) {
				void handleFileSelect(files);
			}
		},
		[handleFileSelect],
	);
	return (
		<div className={styles.container}>
			<CloneAllowedToggle guildId={guildId} kind="emoji" />
			<div className={styles.controls}>
				<Input
					type="text"
					placeholder={t`Search emojis...`}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
					className={styles.searchInput}
				/>
			</div>
			{fetchStatus === 'error' && (
				<div className={styles.notice}>
					<p className={styles.noticeText}>
						<WarningCircleIcon size={32} weight="fill" />
						<Trans>Failed to load emojis. Please try again later.</Trans>
					</p>
				</div>
			)}
			{canCreateExpressions && (
				<>
					<UploadSlotInfo
						title={<Trans>Emoji Slots</Trans>}
						currentCount={staticEmojis.length}
						maxCount={maxStaticEmojis}
						uploadButtonText={<Trans>Upload Emoji</Trans>}
						onUploadClick={async () => {
							const files = await openFilePicker({
								multiple: true,
								accept: getAcceptString('emoji'),
							});
							if (files.length > 0) {
								void handleFileSelect(files);
							}
						}}
						description={
							<Trans>
								Emoji names must be at least 2 characters long and can only contain alphanumeric characters and
								underscores. {getSupportedFormatsLabel(i18n, 'emoji')}. We compress images to 128x128 pixels. Maximum
								size: {Math.round(GlobalLimits.getEmojiMaxSize() / 1024)} KB per emoji.
							</Trans>
						}
						additionalSlots={
							<>
								<span>
									<Trans>
										Static: {staticEmojis.length} /{' '}
										{maxStaticEmojis === Number.POSITIVE_INFINITY ? '∞' : maxStaticEmojis}
									</Trans>
								</span>
								<span>
									<Trans>
										Animated: {animatedEmojis.length} /{' '}
										{maxAnimatedEmojis === Number.POSITIVE_INFINITY ? '∞' : maxAnimatedEmojis}
									</Trans>
								</span>
							</>
						}
					/>
					<UploadDropZone onDrop={handleDrop} description={<Trans>Drag and drop emoji files here</Trans>} />
				</>
			)}
			{searchQuery && filteredEmojis.length === 0 && (
				<div className={styles.notice}>
					<p className={styles.noticeText}>
						<Trans>No emojis found matching your search.</Trans>
					</p>
				</div>
			)}
			{fetchStatus === 'pending' && (
				<div className={styles.spinnerContainer}>
					<Spinner />
				</div>
			)}
			{fetchStatus === 'success' && (staticEmojis.length > 0 || animatedEmojis.length > 0) && (
				<div className={clsx(styles.emojiSections, layout === 'grid' && styles.emojiSectionsGrid)}>
					{staticEmojis.length > 0 && (
						<div className={styles.emojiSection}>
							<h3 className={styles.emojiSectionTitle}>
								<Trans>Non-Animated Emoji ({staticEmojis.length})</Trans>
							</h3>
							{layout === 'list' && <EmojiListHeader />}
							<div className={layout === 'list' ? styles.emojiItemsList : styles.emojiGrid}>
								{staticEmojis.map((emoji: GuildEmojiWithUser) => (
									<EmojiListItem
										key={emoji.id}
										guildId={guildId}
										emoji={emoji}
										layout={layout}
										canModify={canModifyEmoji(emoji)}
										onRename={handleEmojiRename}
										onRemove={handleEmojiDelete}
									/>
								))}
							</div>
						</div>
					)}
					{animatedEmojis.length > 0 && (
						<div className={styles.emojiSection}>
							<h3 className={styles.emojiSectionTitle}>
								<Trans>Animated Emoji ({animatedEmojis.length})</Trans>
							</h3>
							{layout === 'list' && <EmojiListHeader />}
							<div className={layout === 'list' ? styles.emojiItemsList : styles.emojiGrid}>
								{animatedEmojis.map((emoji: GuildEmojiWithUser) => (
									<EmojiListItem
										key={emoji.id}
										guildId={guildId}
										emoji={emoji}
										layout={layout}
										canModify={canModifyEmoji(emoji)}
										onRename={handleEmojiRename}
										onRemove={handleEmojiDelete}
									/>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
});

export default GuildEmojiTab;
